import type { ReviewLink, ReviewLinkTab, RowState } from './types';

/**
 * The rules the Review Links screen runs on, kept free of React so they can be
 * tested on their own. Every function takes `now` rather than reading the
 * clock, for the same reason.
 */

const DAY_MS = 86_400_000;

/** A link's "N days left" turns amber at this many days or fewer. */
export const EXPIRY_WARNING_DAYS = 7;

/** "Approved - N in the last 30 days". */
const RECENT_DAYS = 30;

export function isExpired(link: ReviewLink, now: number): boolean {
  return new Date(link.expiresAt).getTime() <= now;
}

/**
 * The row's one-word state, checked in the design's order.
 *
 * A decision wins over expiry: a link the dentist approved and then let lapse
 * is still "approved" - that is the fact the lab cares about. Only a link with
 * no answer reads as closed once it is revoked or past its date.
 */
export function rowState(link: ReviewLink, now: number): RowState {
  if (link.decision === 'approve') return 'approved';
  if (link.decision === 'reject') return 'rejected';
  if (link.decision === 'changes_requested') return 'changes_requested';
  if (link.revokedAt || isExpired(link, now)) return 'closed';
  return 'awaiting';
}

/** Whole days until expiry, rounded up - "1 day left" means some of today. */
export function daysLeft(link: ReviewLink, now: number): number {
  return Math.ceil((new Date(link.expiresAt).getTime() - now) / DAY_MS);
}

/** Which ⋯ menu actions apply to a link. */
export function availableActions(link: ReviewLink, now: number) {
  const revoked = link.revokedAt !== null;
  const decided = link.decision === 'approve' || link.decision === 'reject';
  return {
    // Resending or revoking a link that is already dead or already answered
    // does nothing useful - and a link that was never mailed has no address.
    sendAgain: link.emailed && !revoked && !decided && !isExpired(link, now),
    revoke: !revoked && !decided && !isExpired(link, now),
    // Extending an expired link re-opens it, which is the point of the action.
    extend: !revoked && !decided,
  };
}

/** Case-insensitive match on patient ref, any tooth, dentist name or email. */
export function matchesSearch(link: ReviewLink, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    link.patientRef.toLowerCase().includes(q) ||
    link.teeth.some((tooth) => tooth.toLowerCase().includes(q)) ||
    (link.dentist.email?.toLowerCase().includes(q) ?? false) ||
    (link.dentist.name?.toLowerCase().includes(q) ?? false)
  );
}

/** Newest sent first. Returns a new array. */
export function sortNewestFirst(links: readonly ReviewLink[]): ReviewLink[] {
  return [...links].sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());
}

export function tabCounts(
  links: readonly ReviewLink[],
  now: number,
): Record<ReviewLinkTab, number> {
  const counts: Record<ReviewLinkTab, number> = {
    all: links.length,
    awaiting: 0,
    changes_requested: 0,
    approved: 0,
    rejected: 0,
    closed: 0,
  };
  for (const link of links) counts[rowState(link, now)] += 1;
  return counts;
}

export interface SummaryCounts {
  awaiting: number;
  expiringSoon: number;
  changesRequested: number;
  approved: number;
  approvedRecently: number;
  patientLinksOpen: number;
  wantsAnotherTime: number;
}

/** The four cards' numbers and footnotes. */
export function summarise(links: readonly ReviewLink[], now: number): SummaryCounts {
  const counts: SummaryCounts = {
    awaiting: 0,
    expiringSoon: 0,
    changesRequested: 0,
    approved: 0,
    approvedRecently: 0,
    patientLinksOpen: 0,
    wantsAnotherTime: 0,
  };

  for (const link of links) {
    const state = rowState(link, now);
    if (state === 'awaiting') {
      counts.awaiting += 1;
      if (daysLeft(link, now) <= EXPIRY_WARNING_DAYS) counts.expiringSoon += 1;
    } else if (state === 'changes_requested') {
      counts.changesRequested += 1;
    } else if (state === 'approved') {
      counts.approved += 1;
      if (link.decidedAt && now - new Date(link.decidedAt).getTime() <= RECENT_DAYS * DAY_MS) {
        counts.approvedRecently += 1;
      }
    }

    if (link.patientLink?.active) {
      counts.patientLinksOpen += 1;
      if (link.patientLink.appointment?.status === 'reschedule_requested') {
        counts.wantsAnotherTime += 1;
      }
    }
  }

  return counts;
}

/** The rows a tab and search leave on screen, newest first. */
export function visibleLinks(
  links: readonly ReviewLink[],
  tab: ReviewLinkTab,
  query: string,
  now: number,
): ReviewLink[] {
  return sortNewestFirst(
    links.filter(
      (link) => (tab === 'all' || rowState(link, now) === tab) && matchesSearch(link, query),
    ),
  );
}

/** "today", "yesterday", "N days ago" - by calendar day, not 24-hour blocks. */
export function relativeDay(iso: string, now: number): string {
  const startOfDay = (t: number) => {
    const d = new Date(t);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };
  const days = Math.round((startOfDay(now) - startOfDay(new Date(iso).getTime())) / DAY_MS);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${String(days)} days ago`;
}

export function plural(count: number, one: string, many: string): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}
